import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Modal from "../Modal";
import TabNavigation from "../common/TabNavigation";
import OrbitalLoader from "../common/OrbitalLoader";
import HeroIcon from "../common/HeroIcon";
import ScriptCard from "../script/ScriptCard";
import GeneratedVideoCard from "../videos/GeneratedVideoCard";
import PatternExamples from "../../pages/pattern/examples";
import PatternOverview from "../../pages/pattern/overview";
import { formatNumber as defaultFormatNumber, getBrandFitLabel, LABEL_TINT_CLASSES } from "../patterns/utils";
import { formatDateTime } from "../../utils/date";
import { brandsService } from "../../services/brands";
import { audiencesService } from "../../services/audiences";
import { parsePaginatedResponse } from "../../utils/pagination";

const IDEA_TABS = ["overview", "pattern", "samples", "scripts", "videos"];
const TAG_CLASS_NAME =
  "inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs border border-theme-shadow bg-theme-secondary text-theme-text opacity-80";

const IdeaDetailModal = ({
  onClose,
  onCreateScript,
  onReject,
  onSkip,
  isGeneratingScript = false,
}) => {
  const navigate = useNavigate();
  const { ideaHash, tab } = useParams();
  const currentTab = IDEA_TABS.includes(tab) ? tab : "overview";
  const basePath = `/ideas/${encodeURIComponent(ideaHash)}/`;

  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patternVideoRows, setPatternVideoRows] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);

  useEffect(() => {
    if (!IDEA_TABS.includes(tab)) {
      navigate(`${basePath}overview/`, { replace: true });
    }
  }, [tab, basePath, navigate]);

  useEffect(() => {
    if (!ideaHash) return;
    setLoading(true);
    setError(null);
    brandsService.getIdea(
      ideaHash,
      (response) => {
        setIdea(response?.data || null);
        setLoading(false);
      },
      () => {
        setError("Could not load this idea.");
        setLoading(false);
      },
    );
  }, [ideaHash]);

  const primaryPattern = useMemo(() => {
    const patterns = Array.isArray(idea?.patterns) ? idea.patterns : [];
    return patterns[0] || null;
  }, [idea]);

  useEffect(() => {
    if (currentTab !== "samples" || !primaryPattern) return;
    const audienceHash = primaryPattern.audience_hash;
    const patternRequestId = primaryPattern.pattern_request_id;
    const patternKey = primaryPattern.pattern_key || primaryPattern.id;
    if (!audienceHash || !patternRequestId || !patternKey) return;

    setLoadingVideos(true);
    audiencesService.getAudienceVideos(
      {
        audience_hash: audienceHash,
        pattern_request_id: patternRequestId,
        pattern_id: patternKey,
        page_size: 1000,
      },
      (response) => {
        const { results } = parsePaginatedResponse(response);
        setPatternVideoRows(results.filter((item) => item?.video));
        setLoadingVideos(false);
      },
      () => setLoadingVideos(false),
    );
  }, [currentTab, primaryPattern]);

  const audiences = useMemo(() => {
    return Array.isArray(idea?.audiences) ? idea.audiences : [];
  }, [idea]);

  const tabItems = useMemo(
    () =>
      IDEA_TABS.map((id) => ({
        id,
        label: id.charAt(0).toUpperCase() + id.slice(1),
        to: `${basePath}${id}/`,
        active: currentTab === id,
      })),
    [basePath, currentTab],
  );

  if (!ideaHash) return null;

  const scripts = Array.isArray(idea?.scripts) ? idea.scripts : [];
  const videos = Array.isArray(idea?.videos) ? idea.videos : [];
  const patterns = Array.isArray(idea?.patterns) ? idea.patterns : [];
  const brandFitLabel = getBrandFitLabel(idea?.brand_fit_score);

  const openScript = (scriptHash) => {
    if (!scriptHash) return;
    navigate(`/scripts/${encodeURIComponent(scriptHash)}/`);
  };

  const openVideo = (videoHash) => {
    if (!videoHash) return;
    navigate(`/videos/${encodeURIComponent(videoHash)}/`);
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-5xl" heightClass="h-[85vh] max-h-[85vh]" noPadding>
      <div className="flex flex-col min-h-0 h-full">
        {loading ? (
          <div className="relative flex flex-1 items-center justify-center">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-6 right-6 text-theme-text opacity-70 hover:opacity-100"
              aria-label="Close idea details"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <OrbitalLoader />
          </div>
        ) : (
          <>
            <div className="flex-shrink-0 px-6 pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 min-h-10">
                <div className="min-w-0 flex-1">
                  <h3 className="text-xl font-semibold text-theme-text truncate">
                    {idea?.emoji ? `${idea.emoji} ` : ""}
                    {idea?.title || "Idea details"}
                  </h3>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto min-h-10">
                  {idea?.queue_status === "queued" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onReject?.(idea)}
                        disabled={isGeneratingScript}
                        className="inline-flex items-center gap-1.5 rounded-md border border-theme-shadow px-3 py-2 text-sm text-theme-text hover:bg-theme-secondary disabled:opacity-50"
                      >
                        <HeroIcon name="x-mark" className="w-4 h-4" />
                        Reject
                      </button>
                      <button
                        type="button"
                        onClick={() => onSkip?.(idea)}
                        disabled={isGeneratingScript}
                        className="inline-flex items-center gap-1.5 rounded-md border border-theme-shadow px-3 py-2 text-sm text-theme-text hover:bg-theme-secondary disabled:opacity-50"
                      >
                        <HeroIcon name="arrow-right" className="w-4 h-4" />
                        Skip
                      </button>
                      <button
                        type="button"
                        onClick={() => onCreateScript?.(idea)}
                        disabled={isGeneratingScript}
                        className="inline-flex items-center gap-1.5 rounded-md bg-theme-primary px-3 py-2 text-sm font-semibold text-theme-button hover:opacity-90 disabled:opacity-50"
                      >
                        <HeroIcon name="document-text" className="w-4 h-4" />
                        {isGeneratingScript ? "Creating..." : "Create"}
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    onClick={onClose}
                    className="ml-1 text-theme-text opacity-70 hover:opacity-100"
                    aria-label="Close idea details"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <TabNavigation items={tabItems} compact />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
              {error ? (
            <p className="text-sm text-theme-text opacity-70">{error}</p>
          ) : !idea ? null : currentTab === "overview" ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-1.5">
                {idea?.brand_name ? (
                  <span className={TAG_CLASS_NAME}>
                    <HeroIcon name="fingerprint" className="w-3.5 h-3.5 opacity-70" />
                    {idea.brand_name}
                  </span>
                ) : null}
                {audiences.map((audience) => (
                  <span key={audience.hash || audience.name} className={TAG_CLASS_NAME}>
                    <HeroIcon name="users" className="w-3.5 h-3.5 opacity-70" />
                    {audience.name || "Unnamed audience"}
                  </span>
                ))}
                {patterns.map((pattern, index) => (
                  <span key={`${pattern.id || pattern.pattern_key}-${index}`} className={TAG_CLASS_NAME}>
                    <HeroIcon name="squares-2x2" className="w-3.5 h-3.5 opacity-70" />
                    {pattern.display_label || pattern.name || "Pattern"}
                  </span>
                ))}
              </div>

              <IdeaOverviewSection
                icon={<span className="text-xl leading-none">{idea.emoji || "💡"}</span>}
                title="The concept"
              >
                <p className="text-sm text-theme-text opacity-80 leading-relaxed whitespace-pre-wrap">
                  {idea.content || "No idea content available yet."}
                </p>
              </IdeaOverviewSection>

              {idea.why_this_works ? (
                <IdeaOverviewSection
                  icon={<HeroIcon name="light-bulb" className="w-5 h-5 text-theme-primary" />}
                  title="Why it should work"
                >
                  <p className="text-sm text-theme-text opacity-80 leading-relaxed">{idea.why_this_works}</p>
                </IdeaOverviewSection>
              ) : null}

              {idea.brand_fit_score != null ? (
                <IdeaOverviewSection
                  icon={<HeroIcon name="fingerprint" className="w-5 h-5 text-theme-primary" />}
                  title="Brand fit"
                >
                  <p className="text-sm text-theme-text leading-relaxed mb-2">
                    <span className="text-xl font-bold">{idea.brand_fit_score}</span>
                    <span className="text-theme-text opacity-80"> / 100</span>
                    {brandFitLabel ? (
                      <span
                        className={`ml-2 font-semibold ${LABEL_TINT_CLASSES[brandFitLabel.tint] ?? "text-theme-text"}`}
                      >
                        {brandFitLabel.emoji} {brandFitLabel.label}
                      </span>
                    ) : null}
                  </p>
                  {idea.brand_fit_reasoning ? (
                    <p className="text-sm text-theme-text opacity-80 leading-relaxed">
                      {idea.brand_fit_reasoning}
                    </p>
                  ) : null}
                </IdeaOverviewSection>
              ) : null}

              {idea.rejection_feedback ? (
                <IdeaOverviewSection
                  icon={<HeroIcon name="x-mark" className="w-6 h-6 text-red-500" />}
                  title="Rejection feedback"
                >
                  <p className="text-sm text-theme-text opacity-80 leading-relaxed">{idea.rejection_feedback}</p>
                </IdeaOverviewSection>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-theme-shadow pt-4">
                <p className="text-xs text-theme-text opacity-50">
                  {idea.create_date ? `Created ${formatDateTime(idea.create_date)}` : ""}
                </p>
                {primaryPattern ? (
                  <Link
                    to={`${basePath}pattern/`}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-theme-primary hover:opacity-80"
                  >
                    Explore the pattern
                    <HeroIcon name="arrow-right" className="w-4 h-4" />
                  </Link>
                ) : null}
              </div>
            </div>
          ) : currentTab === "pattern" ? (
            <div className="space-y-8">
              {patterns.length === 0 ? (
                <p className="text-sm text-theme-text opacity-70">No patterns are linked to this idea.</p>
              ) : (
                patterns.map((pattern, index) => (
                  <PatternOverview
                    key={`${pattern.id || pattern.pattern_key || "pattern"}-${index}`}
                    pattern={pattern}
                    patternRequestId={pattern.pattern_request_id}
                    patternVideoRows={[]}
                    loadingVideos={false}
                    formatNumber={defaultFormatNumber}
                    baseUrl={basePath}
                    showExamples={false}
                    defaultExpanded
                  />
                ))
              )}
            </div>
          ) : currentTab === "samples" ? (
            primaryPattern ? (
              <PatternExamples
                patternVideoRows={patternVideoRows}
                loadingVideos={loadingVideos}
                formatNumber={defaultFormatNumber}
                baseUrl={`/audience/${primaryPattern.audience_hash}/`}
              />
            ) : (
              <p className="text-sm text-theme-text opacity-70">No pattern is linked to this idea.</p>
            )
          ) : currentTab === "scripts" ? (
            <div className="space-y-3">
              {scripts.length === 0 ? (
                <p className="text-sm text-theme-text opacity-70">No scripts are attached to this idea yet.</p>
              ) : (
                scripts.map((script) => (
                  <ScriptCard key={script.hash} script={script} onOpen={openScript} />
                ))
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {videos.length === 0 ? (
                <p className="text-sm text-theme-text opacity-70">No videos are attached to this idea yet.</p>
              ) : (
                videos.map((video) => (
                  <GeneratedVideoCard key={video.hash} video={video} onOpen={openVideo} />
                ))
              )}
            </div>
          )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const IdeaOverviewSection = ({ icon, title, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-4 rounded-2xl border border-theme-shadow bg-theme-main p-5">
    <div className="w-10 h-10 rounded-full bg-theme-secondary flex items-center justify-center flex-shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <h4 className="text-sm font-semibold text-theme-text mb-1.5">{title}</h4>
      {children}
    </div>
  </div>
);

export default IdeaDetailModal;
